import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { GetObligations } from '../../graphql/obligations';
import { ObligationsContext } from '../../context';
import './ObligationsDropdown.scss';

const DOCUMENTOS_TRIBUTARIOS_FAMILY_LABEL = 'Documentos Tributarios';

const ObligationsDropdown = ({
  value,
  onChange,
  type, // type parameter to match default value
  placeholder = "Seleccione Obligación",
  className = "",
  disabled = false,
  id,
  upward = false, // When true, dropdown menu opens above the input
  includeDocumentTypes = false, // When true, adds Documentos, Obligaciones, Créditos options
  includeDocumentosTributarios = false, // When true, shows Documentos Tributarios family (only for CreateRuleModal / MATCH rules)
  showNominas = false, // When true, shows Nóminas option (only for SPEND transactions)
  showCreditCardStatements = false, // When true, shows Estado de Cuenta de Tarjeta de Crédito (SPEND)
  includeRendiciones = false, // When true, includes "Rendición por Regularización Bancaria" in the list
  ...props
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [defaultValue, setDefaultValue] = useState(value);
  const [selectedText, setSelectedText] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const obligationsContext = useContext(ObligationsContext);
  const { selectedCompany } = useSelector((state) => state.companyList);
  const companyId = selectedCompany?.id;
  
  // Check if obligations exist in context
  // Si includeRendiciones es true, NO usar el contexto (siempre hacer query para obtener rendiciones)
  const hasContextData = !includeRendiciones && obligationsContext?.obligations && obligationsContext.obligations.length > 0;
  
  const obligationsQueryInput = useMemo(() => {
    if (includeRendiciones) {
      return companyId ? { includeRendiciones: true, companyId } : { includeRendiciones: true };
    }
    return companyId ? { companyId } : undefined;
  }, [includeRendiciones, companyId]);

  // Fetch from API if not in context
  const { data, fetching } = GetObligations(
    hasContextData ? null : obligationsQueryInput,
    hasContextData // pause query when context has data
  );

  // Get obligations from context or API response
  const obligations = useMemo(() => {
    if (hasContextData) {
      return obligationsContext.obligations;
    }
    if (data?.getObligations) {
      return data.getObligations;
    }
    return [];
  }, [hasContextData, obligationsContext.obligations, data?.getObligations]);

  // Update context when data is received from API (only if not includeRendiciones)
  // No actualizar el contexto cuando includeRendiciones es true para evitar sobrescribir datos
  // Exclude Documentos Tributarios unless explicitly requested (e.g. CreateRuleModal for MATCH rules)
  const obligationsForDisplay = useMemo(() => {
    if (includeDocumentosTributarios) return obligations;
    return (obligations || []).filter(
      (o) => o.familyLabel !== DOCUMENTOS_TRIBUTARIOS_FAMILY_LABEL
    );
  }, [obligations, includeDocumentosTributarios]);

  // Update context when data is received from API
  useEffect(() => {
    if (!includeRendiciones && !hasContextData && data?.getObligations && obligationsContext.setObligations) {
      obligationsContext.setObligations(data.getObligations);
    }
  }, [data?.getObligations, hasContextData, obligationsContext, includeRendiciones]);

  // Document type options (only when includeDocumentTypes is true)
  const documentTypeOptions = useMemo(() => {
    if (!includeDocumentTypes) return [];
    
    const options = [
      {
        key: 'TAX_DOCUMENT',
        value: 'TAX_DOCUMENT',
        text: 'Documentos Tributarios',
        isDocumentType: true,
        isSection: false,
      },
      {
        key: 'BOLETA_HONORARIOS',
        value: 'BOLETA_HONORARIOS',
        text: 'Boleta de Honorarios',
        isDocumentType: true,
        isSection: false,
      },
      {
        key: 'OBLIGATION',
        value: 'OBLIGATION',
        text: 'Obligaciones',
        isDocumentType: true,
        isSection: false,
      },
      {
        key: 'CREDIT',
        value: 'CREDIT',
        text: 'Créditos',
        isDocumentType: true,
        isSection: false,
      },
    ];
    
    // Only add NOMINAS option if showNominas is true (SPEND transactions only)
    if (showNominas) {
      options.push({
        key: 'NOMINAS',
        value: 'NOMINAS',
        text: 'Nóminas',
        isDocumentType: true,
        isSection: false,
      });
    }

    if (showCreditCardStatements) {
      options.push({
        key: 'CREDIT_CARD_STATEMENTS',
        value: 'CREDIT_CARD_STATEMENTS',
        text: 'Estado de Cuenta de Tarjeta de Crédito',
        isDocumentType: true,
        isSection: false,
      });
    }
    
    return options;
  }, [includeDocumentTypes, showNominas, showCreditCardStatements]);

  // Transform obligations to dropdown options, sorted by key and grouped by family
  const options = useMemo(() => {
    const obligationOptions = [];
    
    if (!obligationsForDisplay || obligationsForDisplay.length === 0) {
      // If no obligations but document types are included, return just document types
      return includeDocumentTypes ? documentTypeOptions : [];
    }
    
    // Sort by familyKey first, then by typeKey within each family
    // This ensures proper ordering: 3.2 comes before 3.13 (not 3.13 < 3.2 as decimals)
    const sortedObligations = [...obligationsForDisplay].sort((a, b) => {
      const familyKeyA = a.familyKey || 0;
      const familyKeyB = b.familyKey || 0;
      if (familyKeyA !== familyKeyB) {
        return familyKeyA - familyKeyB;
      }
      const typeKeyA = a.typeKey || 0;
      const typeKeyB = b.typeKey || 0;
      return typeKeyA - typeKeyB;
    });

    // Group by familyLabel and create options with section headers
    let currentFamily = null;
    let _currentFamilyKey = null;

    sortedObligations.forEach((obligation) => {
      // Add section header when family changes
      if (currentFamily !== obligation.familyLabel) {
        currentFamily = obligation.familyLabel;
        _currentFamilyKey = obligation.familyKey;
        
        obligationOptions.push({
          key: `section-${obligation.familyKey}`,
          value: null,
          text: `${obligation.familyKey}. ${obligation.familyLabel}`,
          isSection: true,
          familyKey: obligation.familyKey,
        });
      }

      // Add the obligation option
      obligationOptions.push({
        key: obligation._id,
        value: obligation.type, // Use type (string key like "caja") as the value
        text: obligation.typeLabel, // Display only typeLabel (e.g., "Caja")
        obligation: obligation, // Store full obligation object for reference
        isSection: false,
      });
    });

    // Combine document types (at top) with obligation options
    return includeDocumentTypes 
      ? [...documentTypeOptions, ...obligationOptions]
      : obligationOptions;
  }, [obligationsForDisplay, includeDocumentTypes, documentTypeOptions]);

  // Filter options based on search query
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    
    const query = searchQuery.toLowerCase();
    
    // Filter document type options if included
    const filteredDocumentTypes = includeDocumentTypes
      ? documentTypeOptions.filter(opt => 
          opt.text.toLowerCase().includes(query) || 
          opt.value.toLowerCase().includes(query)
        )
      : [];
    
    // First, find all matching obligations (not options) and sort them properly
    const matchingObligations = obligationsForDisplay.filter(obligation => {
      const key = obligation.key || 0;
      const keyStr = key.toString();
      const familyLabel = (obligation.familyLabel || '').toLowerCase();
      const typeLabel = (obligation.typeLabel || '').toLowerCase();
      const family = (obligation.family || '').toLowerCase();
      const type = (obligation.type || '').toLowerCase();
      
      return keyStr.includes(query) ||
        familyLabel.includes(query) ||
        typeLabel.includes(query) ||
        family.includes(query) ||
        type.includes(query);
    });
    
    // Sort matching obligations by familyKey first, then typeKey
    matchingObligations.sort((a, b) => {
      const familyKeyA = a.familyKey || 0;
      const familyKeyB = b.familyKey || 0;
      if (familyKeyA !== familyKeyB) {
        return familyKeyA - familyKeyB;
      }
      const typeKeyA = a.typeKey || 0;
      const typeKeyB = b.typeKey || 0;
      return typeKeyA - typeKeyB;
    });
    
    // Now build the filtered options structure, grouped by family
    const filteredObligationOptions = [];
    const seenOptionIds = new Set(); // Track added options to prevent duplicates
    let currentFamily = null;
    let _currentFamilyKey = null;
    
    matchingObligations.forEach((obligation) => {
      // Skip if we've already added this obligation
      if (seenOptionIds.has(obligation._id)) {
        return;
      }
      
      // Add section header when family changes
      if (currentFamily !== obligation.familyLabel) {
        currentFamily = obligation.familyLabel;
        _currentFamilyKey = obligation.familyKey;
        
        filteredObligationOptions.push({
          key: `section-${obligation.familyKey}`,
          value: null,
          text: `${obligation.familyKey}. ${obligation.familyLabel}`,
          isSection: true,
          familyKey: obligation.familyKey,
        });
      }
      
      // Add the obligation option
      filteredObligationOptions.push({
        key: obligation._id,
        value: obligation.type,
        text: obligation.typeLabel, // Display only typeLabel (e.g., "Caja")
        obligation: obligation,
        isSection: false,
      });
      
      seenOptionIds.add(obligation._id);
    });
    
    // Combine filtered document types with filtered obligation options
    return [...filteredDocumentTypes, ...filteredObligationOptions];
  }, [obligationsForDisplay, searchQuery, includeDocumentTypes, documentTypeOptions]);

  // Find and set default value based on type parameter
  useEffect(() => {
    if (type && obligationsForDisplay.length > 0 && !defaultValue && !value) {
      // Try to match by type (string key) or typeLabel (enum)
      const matchingObligation = obligationsForDisplay.find(
        (obligation) => 
          obligation.type === type || 
          obligation.typeLabel === type ||
          obligation.type?.toLowerCase() === type?.toLowerCase() ||
          obligation.typeLabel?.toLowerCase() === type?.toLowerCase()
      );
      
      if (matchingObligation && matchingObligation.type) {
        setDefaultValue(matchingObligation.type);
        setSelectedText(matchingObligation.typeLabel); // Display only typeLabel
        // Call onChange to notify parent component of the default selection
        if (onChange) {
          const selectedOption = {
            value: matchingObligation.type, // Use type as value (e.g., "caja")
            obligation: matchingObligation
          };
          // Use setTimeout to avoid calling onChange during render
          setTimeout(() => {
            onChange({}, selectedOption);
          }, 0);
        }
      }
    }
  }, [type, obligationsForDisplay, defaultValue, value, onChange]);

  // Update defaultValue and selectedText when value prop changes
  useEffect(() => {
    if (value !== undefined && value !== null) {
      // Handle "NONE" or empty string as no selection
      if (value === "NONE" || value === "") {
        setDefaultValue(null);
        setSelectedText('');
        return;
      }
      
      setDefaultValue(value);
      const selectedOption = options.find(opt => opt.value === value);
      if (selectedOption) {
        setSelectedText(selectedOption.text);
      } else if (type) {
        // Si no encontramos el valor en las opciones pero tenemos type,
        // usar type como texto de fallback (para mostrar el label correcto)
        setSelectedText(type);
      } else {
        setSelectedText('');
      }
    } else {
      setDefaultValue(null);
      setSelectedText('');
    }
  }, [value, options, type]);

  // Update selectedText when options change and we have a value
  useEffect(() => {
    if (defaultValue && options.length > 0) {
      const selectedOption = options.find(opt => opt.value === defaultValue);
      if (selectedOption) {
        setSelectedText(selectedOption.text);
      }
    }
  }, [defaultValue, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle option selection
  const handleSelect = (option) => {
    setDefaultValue(option.value); // Store type as value
    setSelectedText(option.text); // Display typeLabel to user
    setIsOpen(false);
    setSearchQuery('');
    
    if (onChange) {
      // For document types, pass the value directly
      // For obligations, pass the obligation object
      const changeEvent = {
        value: option.value, // Pass type (string key) as value
        obligation: option.obligation, // Only present for obligation options
        isDocumentType: option.isDocumentType || false, // Flag for document types
      };
      
      // Call onChange with the same signature as ObligacionesInput
      // { event, { value } } format
      onChange({}, changeEvent);
    }
  };

  // Handle input change for search
  const handleInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
  };

  // Handle key navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
      inputRef.current?.blur();
    } else if (e.key === 'Enter' && filteredOptions.length > 0) {
      e.preventDefault();
      // Find first non-section option
      const firstSelectableOption = filteredOptions.find(opt => !opt.isSection);
      if (firstSelectableOption) {
        handleSelect(firstSelectableOption);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      }
    }
  };

  // Handle clearing selection
  const handleClear = () => {
    setDefaultValue(null);
    setSelectedText('');
    setIsOpen(false);
    setSearchQuery('');
    
    if (onChange) {
      onChange({}, { value: "" }); // Pass empty string to clear selection
    }
  };

  const displayValue = isOpen ? searchQuery : selectedText;
  const showPlaceholder = !selectedText && !isOpen;

  return (
    <div 
      ref={dropdownRef}
      className={`obligations-dropdown ${className} ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
      {...props}
    >
      <div className="dropdown-wrapper">
        <input
          ref={inputRef}
          id={id}
          type="text"
          className="dropdown-input"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={showPlaceholder ? placeholder : ''}
          disabled={disabled || fetching}
          autoComplete="off"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          tabIndex={disabled ? -1 : 0}
        />
        {defaultValue && !disabled && (
          <span 
            className="dropdown-clear"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            style={{
              position: 'absolute',
              right: '36px',
              top: '50%',
              transform: 'translateY(-50%)',
              cursor: 'pointer',
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b7280',
              zIndex: 10
            }}
            title="Limpiar selección"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20" width="16" height="16">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 6l8 8M14 6l-8 8"/>
            </svg>
          </span>
        )}
        <span className="dropdown-arrow">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
            <path stroke="#6b7280" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m6 8 4 4 4-4"/>
          </svg>
        </span>
        {fetching && (
          <span className="dropdown-loading">Cargando...</span>
        )}
      </div>
      {isOpen && !disabled && (
        <div className={`dropdown-menu${upward ? ' upward' : ''}`}>
          {filteredOptions.length > 0 ? (
            <ul className="dropdown-options" role="listbox">
              {filteredOptions.map((option) => (
                <li
                  key={option.key}
                  className={`dropdown-option ${option.isSection ? 'section-header' : ''} ${defaultValue === option.value ? 'selected' : ''}`}
                  onClick={() => !option.isSection && handleSelect(option)}
                  role={option.isSection ? 'presentation' : 'option'}
                  aria-selected={option.isSection ? undefined : defaultValue === option.value}
                >
                  <span className="option-text">{option.text}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="dropdown-no-results">
              {searchQuery ? "No se encontraron resultados." : "No hay obligaciones disponibles."}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ObligationsDropdown;
